import { useEffect, useState, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/**
 * Item de navegação da sidebar/drawer do AppShell. `icon` opcional (receba um
 * componente de ícone, ex: Lucide). `active` controla estado visual.
 */
export interface AppShellNavItem {
  id: string;
  label: string;
  href: string;
  icon?: ReactNode;
  active?: boolean;
}

export interface AppShellProps {
  /**
   * Marca exibida na **topbar mobile** (sobre fundo claro do background).
   * Use a versão light da logo aqui — em geral `<Brand />`.
   */
  brand: ReactNode;
  /**
   * Marca exibida na **sidebar desktop** e no **drawer mobile** — ambas
   * têm fundo navy do brand book (token `--sidebar`). Passe a versão
   * dark da logo: `<Brand onDark />`.
   *
   * Se não fornecida, fallback para `brand` — útil em apps que ainda
   * não distinguem variantes (ex: AuthShell).
   */
  brandSidebar?: ReactNode;
  /** Itens de navegação principais (sidebar desktop + drawer mobile). */
  navItems: AppShellNavItem[];
  /**
   * Conteúdo da topbar à direita — tipicamente seletor de clínica, menu de
   * usuário, ThemeToggle, badges de notificação.
   */
  topbarRight?: ReactNode;
  /** Rodapé da sidebar (ex: botão de suporte, versão do app). */
  sidebarFooter?: ReactNode;
  /** Conteúdo principal da rota. Scrollável isoladamente. */
  children: ReactNode;
  /** Classe extra no container `<main>`. */
  mainClassName?: string;
}

/**
 * Shell principal do app autenticado (Superadmin + Admin de Clínica).
 *
 * Layout:
 * - **Desktop (≥ lg)**: sidebar fixa 16rem à esquerda, topbar sticky, main scrollável.
 * - **Mobile (< lg)**: topbar com botão hambúrguer que abre drawer overlay.
 *
 * Safe-area insets aplicadas no topbar (iOS notch) e no drawer.
 * Tap targets ≥ 44px. Foco visível com `ring-ring`.
 */
export function AppShell({
  brand,
  brandSidebar,
  navItems,
  topbarRight,
  sidebarFooter,
  children,
  mainClassName,
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Brand exibido na sidebar (desktop + drawer mobile) sobre fundo navy
  // = versão dark da logo. Fallback para `brand` mantém compat com
  // shells antigas que ainda passam só uma prop.
  const sidebarBrand = brandSidebar ?? brand;

  // Trava scroll do body quando drawer mobile aberto
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [drawerOpen]);

  // Fecha drawer com Esc
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      {/* Sidebar desktop */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-sticky hidden w-64 flex-col bg-sidebar text-sidebar-foreground',
          'lg:flex',
        )}
        aria-label="Navegação principal"
      >
        <div className="flex h-16 items-center px-3 border-b border-sidebar-border">
          {sidebarBrand}
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.id}>
                <SidebarLink item={item} />
              </li>
            ))}
          </ul>
        </nav>
        {sidebarFooter ? (
          <div className="border-t border-sidebar-border p-3">{sidebarFooter}</div>
        ) : null}
      </aside>

      {/* Topbar — em desktop começa DEPOIS da sidebar (`lg:left-64`) para
          não cobrir o brand renderizado dentro da sidebar (z-stacking do
          backdrop-blur sobreporia mesmo com mesmo z-index, porque o
          header vem depois do <aside> na ordem do DOM). Em mobile a
          sidebar está oculta, então o header é full-width. Mantém o
          padding direito de 1rem para o `topbarRight` não colar na
          borda. */}
      <header
        className={cn(
          'fixed top-0 right-0 left-0 z-sticky flex h-16 items-center gap-3 border-b border-border bg-background/80 backdrop-blur',
          'lg:left-64',
          'px-4',
          'pt-[env(safe-area-inset-top)]',
        )}
      >
        <button
          type="button"
          className={cn(
            'inline-flex h-11 w-11 items-center justify-center rounded-md lg:hidden',
            'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
          aria-label="Abrir menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <HamburgerIcon />
        </button>
        <div className="flex items-center gap-2 lg:hidden">{brand}</div>
        <div className="ml-auto flex items-center gap-2">{topbarRight}</div>
      </header>

      {/* Drawer mobile */}
      {drawerOpen ? (
        <div
          className="fixed inset-0 z-modal lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navegação"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-label="Fechar menu"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className={cn(
              'absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-sidebar text-sidebar-foreground',
              'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
            )}
          >
            <div className="flex h-16 items-center justify-between px-3 border-b border-sidebar-border">
              {sidebarBrand}
              <button
                type="button"
                className={cn(
                  'inline-flex h-11 w-11 items-center justify-center rounded-md',
                  'hover:bg-sidebar-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                )}
                aria-label="Fechar menu"
                onClick={() => setDrawerOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3">
              <ul className="flex flex-col gap-1">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <SidebarLink item={item} onNavigate={() => setDrawerOpen(false)} />
                  </li>
                ))}
              </ul>
            </nav>
            {sidebarFooter ? (
              <div className="border-t border-sidebar-border p-3">{sidebarFooter}</div>
            ) : null}
          </aside>
        </div>
      ) : null}

      {/* Main */}
      <main
        className={cn(
          'min-h-dvh pt-16 lg:pl-64',
          'pb-[env(safe-area-inset-bottom)]',
          mainClassName,
        )}
      >
        {children}
      </main>
    </div>
  );
}

function SidebarLink({ item, onNavigate }: { item: AppShellNavItem; onNavigate?: () => void }) {
  return (
    <a
      href={item.href}
      onClick={onNavigate}
      // Sidebar agora é navy do brand book em ambos os temas (token
      // --sidebar). Cores aqui usam --sidebar-foreground (branco-suave)
      // com opacidades para hierarquia: 70% inativo, 100% ativo, 10%
      // bg gold pra item ativo. Barra dourada à esquerda (pseudo
      // `before:`) marca o estado ativo sem alterar layout.
      className={cn(
        'group relative flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        'hover:bg-sidebar-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        item.active
          ? 'bg-accent/15 text-sidebar-foreground before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-accent'
          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground',
      )}
      aria-current={item.active ? 'page' : undefined}
    >
      {item.icon ? <span className="shrink-0">{item.icon}</span> : null}
      <span className="truncate">{item.label}</span>
    </a>
  );
}

function HamburgerIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
